'use client';

import { Box, Container, Typography } from '@mui/material';
import Link from 'next/link';
import CookieActions from './components/CookieActions';
import CookiePreference from './components/CookiePreference';

export default function CookiesZhCN({ settings, handleCookieChange, handleSaveSettings, handleRejectAll, notification }: I18nCookiesProps) {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Cookie 设置
        </Typography>
      </Box>

      {/* 简介 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          我们在RightPulse上使用Cookie来提升您的体验、分析流量并个性化内容和广告。通过点击“接受所有Cookie”，您即同意我们按照隐私政策和Cookie政策中使用Cookie的方式。不过，您可以在下方管理您的Cookie设置。
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          管理您的Cookie偏好
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          我们在平台上使用不同类型的Cookie。请选择您希望允许的Cookie类型：
        </Typography>
      </Box>

      {/* Cookie 类型 */}
      <Box sx={{ mb: 4, pl: 4 }}>
        {/* 严格必要的Cookie */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            1. 严格必要的Cookie
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            这些Cookie对于网站的正常运行至关重要，无法关闭。它们通常在您执行某些操作（如登录或填写表单）时设置。
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            始终启用 —— 您无法禁用这些Cookie。
          </Typography>
        </Box>

        {/* 性能和分析Cookie */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            2. 性能和分析Cookie
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            这些Cookie帮助我们了解访客如何与我们的网站互动，例如您最常访问的页面。我们使用这些信息来改进我们的网站和服务。
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>Google Analytics</li>
            <li>Mixpanel</li>
            <li>其他分析工具</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            您可以随时选择禁用或启用这些Cookie。
          </Typography>
          <CookiePreference 
            label="启用性能Cookie"
            type="performance"
            initialValue={settings.performance}
            onChange={handleCookieChange}
          />
        </Box>

        {/* 功能性Cookie */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            3. 功能性Cookie
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            这些Cookie允许我们记住您的偏好（如语言或地区），并提供更增强、更个性化的功能。
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>语言偏好</li>
            <li>登录详情</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            您可以随时选择禁用或启用这些Cookie。
          </Typography>
          <CookiePreference 
            label="启用功能性Cookie"
            type="functional"
            initialValue={settings.functional}
            onChange={handleCookieChange}
          />
        </Box>

        {/* 广告和定向Cookie */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.primary" fontWeight={600} sx={{ mb: 2 }}>
            4. 广告和定向Cookie
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            这些Cookie用于根据您的浏览习惯投放个性化广告。它们还用于限制您看到同一广告的次数，并帮助我们衡量营销活动的效果。
          </Typography>
          <ul style={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
            <li>Google AdSense</li>
            <li>Facebook Pixel</li>
            <li>其他广告网络</li>
          </ul>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            您可以随时选择禁用或启用这些Cookie。
          </Typography>
          <CookiePreference 
            label="启用广告Cookie"
            type="advertising"
            initialValue={settings.advertising}
            onChange={handleCookieChange}
          />
        </Box>
      </Box>

      {/* 其他信息 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          如何管理您的Cookie设置
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          您可以随时访问我们的Cookie设置页面更新您的偏好。如果您禁用了某些Cookie，网站的部分功能可能无法正常运行。
        </Typography>
      </Box>

      {/* 同意声明 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          同意
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          如果您希望限制我们使用的Cookie类型，请在上方的选项中进行选择并点击“确认”。
        </Typography>
      </Box>

      {/* 了解更多 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          了解更多
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          如果您想了解更多关于我们如何保护您的隐私的信息，请查看我们的{' '}
          <Link 
            href="/legal/privacy" 
            style={{ 
              color: 'primary.main', 
              textDecoration: 'underline',
            }}
          >
            隐私政策
          </Link>
          。
        </Typography>
      </Box>

      {/* 操作按钮 */}
      <CookieActions 
        onReject={handleRejectAll}
        onSave={handleSaveSettings}
      />

      {/* 通知 */}
      {notification}
    </Container>
  );
}
